import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import { Company } from 'src/types/Companies';
import ComposeMessage from './ComposeMessage';
import {
  CLIENT_COMPANY_MAPPINGS,
  ClientCompanyMapping,
  DEFAULT_ITEM,
} from './constants/clientCompanyMappings';
import { useCompanyInitialization } from './hooks/useCompanyInitialization';

// Component constants
const LOCAL_STORAGE_KEY = 'selectedClientId';
const DROPDOWN_POPUP_HEIGHT = 500;
const NAVBAR_STYLES = {
  container: 'message-layout navbar-container',
  dropdownWrapper: 'flex',
  dropdown: 'company-selector-dropdown',
  composeWrapper: 'flex items-center pr-5',
} as const;

// Custom inline styles for enhanced dropdown styling
const DROPDOWN_CUSTOM_STYLES = {
  minWidth: '600px', // Twice as wide as typical dropdown
  backgroundColor: '#fefce8', // Light yellow background
  borderRadius: '4px',
  border: '2px solid black', // 2-pixel black border
  fontSize: 'calc(1rem + 4px)', // 4 pixels larger than default
  fontWeight: 'bold', // Bold font weight
} as const;

// Client ID change event type
interface ClientIdChangeEvent extends CustomEvent {
  detail: { clientId: string };
}

// Error types
type NavbarError = {
  type: 'localStorage' | 'context' | 'validation';
  message: string;
};

/**
 * ViewMessageNavbar component providing company selection and compose functionality
 * Supports both unified and legacy context patterns with intelligent mode detection
 */
const ViewMessageNavbar = () => {
  const { t } = useTranslation();
  const { token, clientId, companyId, companyName } = useAuth();
  const { changeCompany, company } = useCompany();
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
  const [error, setError] = useState<NavbarError | null>(null);
  const [isUserSelected, setIsUserSelected] = useState<boolean>(false); // Track if user manually selected

  // Get pre-parsed auth values from useAuth hook (AuthContext already handles token parsing)
  const authTokenInfo = useMemo(() => {
    if (!clientId && !companyId) {
      return null;
    }

    return {
      clientId: clientId || '',
      companyId: companyId || '',
      companyName: companyName || '',
    };
  }, [clientId, companyId, companyName]);

  // Clear error handler
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Safe localStorage operations with error handling
  const getStoredClientId = useCallback((): string | null => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      return stored && stored !== 'null' && stored !== 'undefined' ? stored : null;
    } catch (err) {
      console.warn('Failed to read from localStorage:', err);
      setError({ type: 'localStorage', message: 'Unable to read stored company selection' });
      return null;
    }
  }, [setError]);

  const setStoredClientId = useCallback(
    (clientId: string | null) => {
      try {
        if (clientId) {
          localStorage.setItem(LOCAL_STORAGE_KEY, clientId);
        } else {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        }
      } catch (err) {
        console.warn('Failed to write to localStorage:', err);
        setError({ type: 'localStorage', message: 'Unable to save company selection' });
      }
    },
    [setError]
  );

  // Get mapping object based on selected clientId with validation
  const selectedMapping = useMemo(() => {
    if (!selectedClientId) return DEFAULT_ITEM;

    const mapping = CLIENT_COMPANY_MAPPINGS.find((m) => m.clientId === selectedClientId);
    if (!mapping) {
      setError({ type: 'validation', message: 'Selected company is no longer available' });
      return DEFAULT_ITEM;
    }

    return mapping;
  }, [selectedClientId, setError]);

  // Display mapping for "Selected from dropdown" - shows AUTH_TOKEN values when auto-selected, selected values when user selected
  const displayMapping = useMemo(() => {
    // If user manually selected company from dropdown, always show the selected mapping values
    if (isUserSelected) {
      return selectedMapping;
    }

    // If auto-selected and we have AUTH_TOKEN info, use AUTH_TOKEN values for display
    if (
      authTokenInfo?.clientId &&
      authTokenInfo?.companyId &&
      selectedMapping &&
      selectedMapping.clientId !== ''
    ) {
      const authDisplay = {
        clientId: authTokenInfo.clientId,
        companyId: authTokenInfo.companyId,
        companyName: authTokenInfo.companyName || selectedMapping.companyName,
      };
      return authDisplay;
    }

    // Otherwise use the selected mapping
    return selectedMapping;
  }, [authTokenInfo, selectedMapping, isUserSelected]);

  // Dispatch client ID change event with error handling
  const dispatchClientIdChange = useCallback((clientId: string) => {
    try {
      const event: ClientIdChangeEvent = new CustomEvent('clientIdChanged', {
        detail: { clientId },
      });
      window.dispatchEvent(event);
    } catch (err) {
      console.warn('Failed to dispatch clientIdChanged event:', err);
    }
  }, []);

  // Clear previous state when JWT token changes
  useEffect(() => {
    // Clear selected client ID state
    setSelectedClientId('');

    // Clear user selection flag
    setIsUserSelected(false);

    // Clear localStorage
    setStoredClientId(null);

    // Clear any errors
    setError(null);

    // Notify other components of the change
    dispatchClientIdChange('');
  }, [token, setStoredClientId, dispatchClientIdChange]);

  // Initialize company selection with multiple priority levels
  useCompanyInitialization({
    isUserSelected,
    authTokenInfo,
    company,
    getStoredClientId,
    setStoredClientId,
    clearError,
    setError,
    changeCompany,
    dispatchClientIdChange,
    setSelectedClientId,
    setIsUserSelected,
  });

  const handleCompanyChange = useCallback(
    (event: DropDownListChangeEvent) => {
      try {
        clearError(); // Clear any previous errors
        const selectedItem = event.target.value as ClientCompanyMapping;

        // Validate selected item structure
        if (!selectedItem || typeof selectedItem !== 'object') {
          setError({ type: 'validation', message: 'Invalid company selection' });
          return;
        }

        // Handle case when default item selected (empty clientId)
        if (!selectedItem.clientId || selectedItem.clientId === '') {
          setSelectedClientId('');
          setIsUserSelected(true); // Mark as user action
          setStoredClientId(null);
          dispatchClientIdChange('');
          return;
        }

        // Validate selected item exists in our mappings
        const validMapping = CLIENT_COMPANY_MAPPINGS.find(
          (m) => m.clientId === selectedItem.clientId
        );

        if (!validMapping) {
          setError({ type: 'validation', message: 'Selected company is not available' });
          return;
        }

        // Handle valid selection
        setSelectedClientId(selectedItem.clientId);
        setIsUserSelected(true); // Mark as user-selected

        // Update company context with validation
        const companyData: Company = {
          companyId: selectedItem.companyId,
          companyName: selectedItem.companyName,
        };

        try {
          changeCompany(companyData);
        } catch (err) {
          console.error('Failed to change company:', err);
          setError({ type: 'context', message: 'Failed to update company context' });
          return;
        }

        //Store clientId for persistence
        setStoredClientId(selectedItem.clientId);

        //Notify other components of change
        dispatchClientIdChange(selectedItem.clientId);
      } catch (err) {
        console.error('Company change error:', err);
        setError({ type: 'validation', message: 'An unexpected error occurred' });
      }
    },
    [changeCompany, clearError, setStoredClientId, dispatchClientIdChange]
  );

  // Error display handler
  const handleErrorDismiss = useCallback(() => {
    clearError();
    if (error?.type === 'validation') {
      // Reset to default selection on validation errors
      setSelectedClientId('');
      setIsUserSelected(false); // Reset user selection state
      setStoredClientId(null);
      dispatchClientIdChange('');
    }
  }, [
    error,
    clearError,
    setStoredClientId,
    dispatchClientIdChange,
    setIsUserSelected,
    setSelectedClientId,
  ]);

  // Show error toast for critical errors
  useEffect(() => {
    if (error && (error.type === 'localStorage' || error.type === 'context')) {
      toast.error(error.message, {
        toastId: `navbar-error-${error.type}`, // Prevent duplicate toasts
        onClose: handleErrorDismiss,
      });
    }
  }, [error, handleErrorDismiss]);

  return (
    <>
      <div className={NAVBAR_STYLES.container}>
        <div className={`${NAVBAR_STYLES.dropdownWrapper} hidden`}>
          <DropDownList
            className={NAVBAR_STYLES.dropdown}
            style={DROPDOWN_CUSTOM_STYLES}
            data={CLIENT_COMPANY_MAPPINGS}
            textField="companyName"
            dataItemKey="clientId"
            value={selectedMapping}
            onChange={handleCompanyChange}
            disabled={!!error}
            aria-label={t('Select Company')}
            aria-describedby="company-selector-description"
            aria-invalid={!!error}
            popupSettings={{ height: DROPDOWN_POPUP_HEIGHT }}
            title={error ? error.message : t('Select a company to view messages')}
          />
          <div id="company-selector-description" className="sr-only">
            {t('Choose a company from the dropdown to view and manage messages')}
          </div>
        </div>
        {/* Values from current dropdown selected company - Hidden */}
        {selectedClientId && displayMapping && displayMapping.clientId !== '' && (
          <div
            className="selected-value-display ml-4 flex flex-col bg-gray-100 px-3 py-1 rounded border hidden"
            style={{ minWidth: '170px' }}
          >
            <span className="text-xs text-gray-700 font-medium mb-1.5">Selected from dropdown</span>
            <div className="flex">
              <span className="text-xs text-gray-600 mr-3">
                ClientId <strong>{displayMapping.clientId}</strong>
              </span>
              <span className="text-xs text-gray-600">
                CompanyId <strong>{displayMapping.companyId}</strong>
              </span>
            </div>
          </div>
        )}
        {/* Error indicator */}
        {error && error.type === 'validation' && (
          <div className="error-indicator ml-2 text-red-600" role="alert" aria-live="polite">
            <span className="text-sm">{error.message}</span>
            <button
              className="ml-2 text-xs underline"
              onClick={handleErrorDismiss}
              aria-label={t('Dismiss error')}
            >
              {t('Dismiss')}
            </button>
          </div>
        )}
      </div>

      <ComposeMessage
        visible={isComposeModalOpen}
        closeModal={() => setIsComposeModalOpen(false)}
      />
    </>
  );
};

export default ViewMessageNavbar;
